import React from "react";
import { Box, Button, Tooltip, Typography, useTheme } from "@mui/material";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";
import "dayjs/locale/it";
import {
  AlertTriangle,
  Clock,
  GraduationCap,
  MapPin,
  Video,
} from "lucide-react";
import BadgeBoxCst from "@components/BadgeBoxCst";
import MoreActionsMenuCst from "@components/MoreActionsMenuCst";
import { ListItemAction } from "@components/ListItemCardCst/types";
import { EditionKind, EditionSession } from "../types";
import {
  getSessionLink,
  isAbsoluteHttpLink,
  truncateLink,
} from "../sessionLink";
import { ButtonPrimary } from "src/UI/ButtonPrimary";
import { ButtonSecondary } from "src/UI/ButtonSecondary";

interface SessionCardProps {
  session: EditionSession;
  kind: EditionKind;
  onVerifyInvites?: (session: EditionSession) => void;
  onDetails?: (session: EditionSession) => void;
  menuActions?: ListItemAction[] | ListItemAction[][];
}

const formatDateTime = (start?: number, end?: number): string => {
  if (!start) return "";
  const s = dayjs(start).locale("it");
  const e = end ? dayjs(end).locale("it") : null;
  const datePart = s.format("D MMMM YYYY");
  const timePart = e
    ? `${s.format("HH:mm")} - ${e.format("HH:mm")}`
    : s.format("HH:mm");
  return `${datePart} | ${timePart}`;
};

const joinLabels = (refs?: { label: string }[] | null): string =>
  (refs ?? []).map((r) => r.label).join(", ");

const SessionCard: React.FC<SessionCardProps> = ({
  session,
  kind,
  onVerifyInvites,
  onDetails,
  menuActions,
}) => {
  const { t } = useTranslation(["edition-detail", "input"]);
  const theme = useTheme();

  const isVirtual = kind === "virtual";
  const faculties = joinLabels(session.faculties);
  const tutors = joinLabels(session.tutors);
  const toolLabel = session.tool
    ? t(`options.meeting_tool.${session.tool.toLowerCase()}`, {
        ns: "input",
      })
    : "";
  const sessionLink = getSessionLink(session);
  const displayLink = truncateLink(sessionLink);
  const isLinkTruncated = displayLink !== sessionLink;
  const hasVirtualInfo = !!(toolLabel || sessionLink);
  const physicalInfo = [session.site?.label, joinLabels(session.rooms)]
    .filter(Boolean)
    .join(" — ");

  const rowIcon = (icon: React.ReactNode, content: React.ReactNode) => (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: "0.75rem",
        color: theme.palette.text.primary,
        fontSize: "13px",
      }}
    >
      <Box width={"1.25rem"} display={"flex"} alignItems={"center"}>
        {icon}
      </Box>
      {content}
    </Box>
  );

  const linkValue = () => (
    <Tooltip title={isLinkTruncated ? sessionLink : ""}>
      {isAbsoluteHttpLink(sessionLink) ? (
        <Typography
          component="a"
          href={sessionLink}
          target="_blank"
          rel="noopener noreferrer"
          sx={{
            fontSize: "inherit",
            color: "inherit",
            textDecoration: "none",
            "&:hover": { textDecoration: "underline" },
          }}
        >
          {displayLink}
        </Typography>
      ) : (
        <Typography component="span" sx={{ fontSize: "inherit" }}>
          {displayLink}
        </Typography>
      )}
    </Tooltip>
  );

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "stretch",
        justifyContent: "space-between",
        gap: 2,
        minHeight: "6.5rem",
        padding: "1rem",
        borderRadius: "0.5rem",
        backgroundColor: theme.palette.backgroundPrimaryControl,
        [theme.breakpoints.down("lg")]: {
          flexDirection: "column",
          alignItems: "stretch",
          gap: 1.5,
        },
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 0.75,
          minWidth: 0,
          justifyContent: "space-between",
        }}
      >
        <Box>
          {rowIcon(
            <Clock size={"1rem"} />,
            <Typography
              sx={{
                fontSize: "0.875rem",
                fontWeight: 700,
                color: theme.palette.exploreCardText,
              }}
            >
              {formatDateTime(session.start_date, session.end_date)}
            </Typography>,
          )}

          {isVirtual
            ? hasVirtualInfo &&
              rowIcon(
                <Video size={"1rem"} />,
                <Typography
                  sx={{
                    fontSize: "0.875rem",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {toolLabel}
                  {toolLabel && sessionLink ? " | " : ""}
                  {sessionLink && linkValue()}
                </Typography>,
              )
            : !!physicalInfo &&
              rowIcon(
                <MapPin size={"1rem"} />,
                <Typography sx={{ fontSize: "0.875rem" }}>
                  {physicalInfo}
                </Typography>,
              )}
        </Box>

        {(faculties || tutors) &&
          rowIcon(
            <GraduationCap size={"1rem"} />,
            <Typography sx={{ fontSize: "0.76875rem" }}>
              {[
                faculties && t("session.faculties", { names: faculties }),
                tutors && t("session.tutors", { names: tutors }),
              ]
                .filter(Boolean)
                .join("   ")}
            </Typography>,
          )}
      </Box>

      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "flex-end",
          justifyContent: "space-between",
          gap: 2,
          flexShrink: 0,
          [theme.breakpoints.down("lg")]: {
            width: "100%",
            alignItems: "stretch",
          },
          maxWidth: "32%",
        }}
      >
        <Box
          sx={{
            [theme.breakpoints.down("lg")]: { alignSelf: "flex-start" },
          }}
        >
          {!session.confirmed && (
            <BadgeBoxCst
              variant="warning"
              icon={<AlertTriangle size={"1rem"} />}
            >
              {t("session.mail-to-send")}
            </BadgeBoxCst>
          )}
        </Box>

        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            [theme.breakpoints.down("lg")]: {
              justifyContent: "flex-start",
              flexWrap: "wrap",
            },
          }}
        >
          <ButtonPrimary onClick={() => onVerifyInvites?.(session)}>
            {t("session.verify-invites")}
          </ButtonPrimary>
          <ButtonSecondary onClick={() => onDetails?.(session)}>
            {t("session.details")}
          </ButtonSecondary>
          {menuActions && (
            <MoreActionsMenuCst
              actions={menuActions}
              ariaLabel="session actions"
            />
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default SessionCard;
